import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook personalizado para gerenciar ações em agendamentos
 * 
 * @param {Object} salonConfig - Configuração do salão
 * @returns {Object} - Funções e estados para gerenciar agendamentos
 */
export const useAppointmentActions = (salonConfig) => {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [atendimentoRealizado, setAtendimentoRealizado] = useState('pendente');
  const [motivoNaoRealizado, setMotivoNaoRealizado] = useState('no_show');
  const [pagamentoConfirmado, setPagamentoConfirmado] = useState(false);
  const [profissionalConfirmadoId, setProfissionalConfirmadoId] = useState(null);
  
  /**
   * Seleciona um evento para exibir detalhes
   * @param {Object} event - Evento selecionado
   */
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    
    // Definir o estado inicial com base nos dados do agendamento
    if (event.appointment.noShow) {
      setAtendimentoRealizado('nao_realizado');
      setMotivoNaoRealizado('no_show');
      setPagamentoConfirmado(false);
    } else if (event.appointment.profissionalConfirmado) {
      setAtendimentoRealizado('realizado');
      setPagamentoConfirmado(event.appointment.pagamentoConfirmado || false);
      // Definir o ID do profissional que confirmou o atendimento
      // Se não existir, define o profissional atual do agendamento
      setProfissionalConfirmadoId(event.appointment.profissionalConfirmadoId || event.resourceId);
    } else {
      setAtendimentoRealizado('pendente');
      setPagamentoConfirmado(event.appointment.pagamentoConfirmado || false);
      setProfissionalConfirmadoId(event.resourceId);
    }
  };
  
  /**
   * Fecha o tooltip de detalhes do agendamento
   */
  const closeTooltip = () => {
    setSelectedEvent(null);
    setAtendimentoRealizado('pendente');
    setMotivoNaoRealizado('no_show');
    setPagamentoConfirmado(false);
    setProfissionalConfirmadoId(null);
  };
  
  /**
   * Salva as alterações feitas no agendamento
   */
  const saveAppointmentChanges = async () => {
    if (!selectedEvent || !selectedEvent.id) return;
    
    try {
      setIsSaving(true);
      
      // Verificar se salonConfig existe
      if (!salonConfig || !salonConfig.id) {
        throw new Error('Configuração do salão não disponível');
      }
      
      // Criar referência para o documento do agendamento
      const appointmentRef = doc(db, `salons/${salonConfig.id}/appointments`, selectedEvent.id);
      
      // Preparar os dados a serem atualizados com base no estado atual
      const updateData = {
        updatedAt: new Date()
      };
      
      // Determinar os dados a atualizar com base no atendimentoRealizado
      if (atendimentoRealizado === 'realizado') {
        updateData.profissionalConfirmado = true;
        updateData.profissionalConfirmadoId = profissionalConfirmadoId;
        updateData.pagamentoConfirmado = pagamentoConfirmado;
        updateData.noShow = false;
      } else if (atendimentoRealizado === 'nao_realizado') {
        updateData.profissionalConfirmado = false;
        updateData.pagamentoConfirmado = false;
        
        if (motivoNaoRealizado === 'no_show') {
          updateData.noShow = true;
        } else {
          updateData.noShow = false;
          updateData.motivoCancelamento = motivoNaoRealizado;
        }
      } else { // pendente
        updateData.profissionalConfirmado = false;
        updateData.noShow = false;
        updateData.pagamentoConfirmado = pagamentoConfirmado;
      }
      
      // Atualizar o documento no Firebase
      await updateDoc(appointmentRef, updateData);
      
      // Atualizar o objeto selecionado localmente
      setSelectedEvent({
        ...selectedEvent,
        appointment: {
          ...selectedEvent.appointment,
          ...updateData
        }
      });
      
      alert('Alterações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar alterações no agendamento:', error);
      alert('Erro ao salvar alterações. Por favor, tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };
  
  /**
   * Calcula a duração entre dois horários no formato "HH:MM"
   * @param {string} startTime - Horário de início
   * @param {string} endTime - Horário de término
   * @returns {number} - Duração em minutos
   */
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    return endMinutes - startMinutes;
  };
  
  return {
    selectedEvent,
    setSelectedEvent,
    atendimentoRealizado,
    setAtendimentoRealizado,
    motivoNaoRealizado,
    setMotivoNaoRealizado,
    pagamentoConfirmado,
    setPagamentoConfirmado,
    profissionalConfirmadoId,
    setProfissionalConfirmadoId,
    isSaving,
    handleSelectEvent,
    closeTooltip,
    saveAppointmentChanges,
    calculateDuration
  };
};

export default useAppointmentActions; 